import { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useCourse } from '@/hooks/useCourses';
import { useLessonProgress, useToggleLessonCompletion } from '@/hooks/useLessonProgress';
import { useUserRole } from '@/hooks/useUserRole';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import {
  ChevronLeft,
  Lock,
  MoreVertical,
  Pencil,
  Layers,
  Settings,
  Eye,
} from 'lucide-react';
import { useCourseEditing } from '@/hooks/useCourseEditing';

// Lazy-loaded admin sheets — only fetched when an admin opens edit mode and
// triggers the menu. Keeps the main askjds bundle free of Gumlet upload code,
// drag-and-drop, and the heavy admin chunks for non-admin users.
const LazyCourseEditSheet = lazy(() =>
  import('@/components/admin/CourseEditSheet').then((m) => ({
    default: m.CourseEditSheet,
  }))
);
const LazyModuleEditSheet = lazy(() =>
  import('@/components/admin/ModuleEditSheet').then((m) => ({
    default: m.ModuleEditSheet,
  }))
);
const LazyLessonEditSheet = lazy(() =>
  import('@/components/admin/LessonEditSheet').then((m) => ({
    default: m.LessonEditSheet,
  }))
);
const LazyCourseContentTree = lazy(() =>
  import('@/components/admin/CourseContentTree').then((m) => ({
    default: m.CourseContentTree,
  }))
);
import {
  getCourseProgress,
  getPublishedLessonsFlat,
  getAllLessonsFlat,
  getAdjacentLessons,
  findModuleForLesson,
} from '@/types/courses';
import type { CourseLesson } from '@/types/courses';
import { PlayerVideoSection } from './player/PlayerVideoSection';
import { PlayerControls } from './player/PlayerControls';
import { PlayerLessonContent } from './player/PlayerLessonContent';
import { PlayerSidebarTOC } from './player/PlayerSidebarTOC';
import { PlayerMobileNav } from './player/PlayerMobileNav';
import { PlayerMobileTOC } from './player/PlayerMobileTOC';

interface CoursePlayerProps {
  basePath: string;
  courseType: 'standard' | 'nextgen';
}

export function CoursePlayer({ basePath, courseType }: CoursePlayerProps) {
  const { courseId, lessonId } = useParams<{
    courseId: string;
    moduleId?: string;
    lessonId?: string;
  }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: course, isLoading } = useCourse(courseId);
  const { data: completedLessons = {} } = useLessonProgress();
  const toggleCompletion = useToggleLessonCompletion();
  const { data: userRole } = useUserRole();

  const isAdmin = userRole?.canEditCourses === true;
  const editMode = isAdmin && searchParams.get('edit') === '1';

  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [showMobileTOC, setShowMobileTOC] = useState(false);

  // Course-editing orchestration. Always called (hooks rule), but the
  // underlying admin query is gated by `enabled: editMode` so non-admins
  // and read-only viewers don't trigger the admin fetch.
  const editing = useCourseEditing(courseId, { enabled: editMode });

  // Local sheet state for "Manage Modules" (CourseContentTree wrapper).
  // Sheet body lands in P3c — this task only wires the menu → state plumbing.
  const [isManageModulesOpen, setIsManageModulesOpen] = useState(false);

  // Published lessons flat list
  const publishedLessons = useMemo(
    () => (course ? getPublishedLessonsFlat(course) : []),
    [course]
  );

  // All lessons (admin edit-mode only)
  const allLessons = useMemo(
    () => (course ? getAllLessonsFlat(course) : []),
    [course]
  );

  // Effective lesson list for navigation respects edit mode
  const navigableLessons = editMode ? allLessons : publishedLessons;

  const handleExitEdit = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  };

  // Currently selected lesson
  const selectedLesson = useMemo(() => {
    if (!lessonId || !course) return null;
    return course.modules
      .flatMap((m) => m.lessons)
      .find((l) => l.id === lessonId) || null;
  }, [course, lessonId]);

  // Auto-navigate to first lesson when course loads without a lesson selected.
  // In edit mode, respect ALL statuses; otherwise only Published.
  useEffect(() => {
    if (!course || lessonId) return;
    const candidates = editMode ? allLessons : publishedLessons;
    if (candidates.length > 0) {
      const first = candidates[0];
      const mod = findModuleForLesson(course, first.id);
      if (mod) {
        const search = searchParams.toString();
        const suffix = search ? `?${search}` : '';
        navigate(`${basePath}/${course.id}/${mod.id}/${first.id}${suffix}`, {
          replace: true,
        });
      }
    }
  }, [
    course,
    lessonId,
    publishedLessons,
    allLessons,
    editMode,
    basePath,
    navigate,
    searchParams,
  ]);

  // Auto-expand module containing selected lesson
  useEffect(() => {
    if (!course || !lessonId) {
      // Expand first module by default
      if (course?.modules.length) {
        setExpandedModules(new Set([course.modules[0].id]));
      }
      return;
    }
    const mod = findModuleForLesson(course, lessonId);
    if (mod) {
      setExpandedModules((prev) => {
        const next = new Set(prev);
        next.add(mod.id);
        return next;
      });
    }
  }, [course, lessonId]);

  // Progress
  const progress = useMemo(
    () => (course ? getCourseProgress(course, completedLessons) : { total: 0, done: 0, percent: 0 }),
    [course, completedLessons]
  );

  // Adjacent lessons. In edit mode, navigation includes all statuses.
  const { previous: previousLesson, next: nextLesson } = useMemo(
    () =>
      course && selectedLesson
        ? getAdjacentLessons(course, selectedLesson.id, editMode)
        : { previous: null, next: null },
    [course, selectedLesson, editMode]
  );

  const toggleModule = (modId: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(modId)) next.delete(modId);
      else next.add(modId);
      return next;
    });
  };

  const navigateToLesson = (lesson: CourseLesson) => {
    if (!course) return;
    const mod = findModuleForLesson(course, lesson.id);
    if (mod) {
      const search = searchParams.toString();
      const suffix = search ? `?${search}` : '';
      navigate(`${basePath}/${course.id}/${mod.id}/${lesson.id}${suffix}`);
    }
  };

  const handleToggleComplete = () => {
    if (!selectedLesson || !courseId) return;
    const mod = findModuleForLesson(course!, selectedLesson.id);
    toggleCompletion.mutate({
      lessonId: selectedLesson.id,
      courseId,
      courseType,
      isCompleted: !!completedLessons[selectedLesson.id],
      lessonTitle: selectedLesson.title,
      moduleId: mod?.id,
    });
  };

  const handleCompleteAndNext = () => {
    if (selectedLesson && courseId && !completedLessons[selectedLesson.id]) {
      const mod = findModuleForLesson(course!, selectedLesson.id);
      toggleCompletion.mutate({
        lessonId: selectedLesson.id,
        courseId,
        courseType,
        isCompleted: false,
        lessonTitle: selectedLesson.title,
        moduleId: mod?.id,
      });
    }
    if (nextLesson) navigateToLesson(nextLesson);
  };

  const handleToggleLessonComplete = (e: React.MouseEvent, lid: string) => {
    e.stopPropagation();
    if (!courseId || !course) return;
    const lesson = navigableLessons.find((l) => l.id === lid);
    const mod = findModuleForLesson(course, lid);
    toggleCompletion.mutate({
      lessonId: lid,
      courseId,
      courseType,
      isCompleted: !!completedLessons[lid],
      lessonTitle: lesson?.title,
      moduleId: mod?.id,
    });
  };

  const handleVideoEnded = () => {
    if (selectedLesson && courseId && !completedLessons[selectedLesson.id]) {
      const mod = findModuleForLesson(course!, selectedLesson.id);
      toggleCompletion.mutate({
        lessonId: selectedLesson.id,
        courseId,
        courseType,
        isCompleted: false,
        lessonTitle: selectedLesson.title,
        moduleId: mod?.id,
      });
    }
  };

  // Loading
  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6 animate-pulse">
        <div className="h-7 w-64 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
        <div className="aspect-video bg-gray-200 dark:bg-gray-700 rounded-xl mb-4" />
        <div className="h-10 w-full bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
    );
  }

  // Course not found
  if (!course) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-500">
        <p className="mb-4">Course not found.</p>
        <Button variant="outline" onClick={() => navigate(basePath)}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Courses
        </Button>
      </div>
    );
  }

  // All lessons Coming Soon — empty state. In edit mode, bypass the lock and
  // show an admin-friendly empty state instead so the editor surface is reachable.
  if (publishedLessons.length === 0 && !editMode) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6 pb-20">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
          {course.title}
        </h2>
        <div className="bg-black aspect-video flex items-center justify-center rounded-xl mb-6">
          <div className="text-center text-gray-400">
            <Lock className="h-16 w-16 mx-auto mb-2" />
            <p className="text-sm">No lessons available yet</p>
          </div>
        </div>
        <div className="text-center py-12 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
          <Lock className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
            Course Coming Soon
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto">
            All lessons in this course are still being prepared. Check back soon
            for updates.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate(basePath)}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Course Library
          </Button>
        </div>
      </div>
    );
  }

  // Admin edit mode + course has zero lessons at all — show editor-friendly hint.
  if (editMode && allLessons.length === 0) {
    return (
      <>
        <div className="sticky top-0 z-30 h-10 flex items-center justify-between px-4 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-900/60 text-sm text-amber-900 dark:text-amber-100">
          <span className="font-medium">
            Editing as admin · changes are live
          </span>
          <button
            type="button"
            onClick={handleExitEdit}
            className="text-amber-900 dark:text-amber-100 hover:underline font-medium"
          >
            Exit
          </button>
        </div>
        <div className="max-w-6xl mx-auto px-4 py-6 pb-20">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
              {course.title}
            </h2>
            {course.status !== 'Published' && (
              <Badge variant="outline" className="text-[10px]">
                {course.status}
              </Badge>
            )}
          </div>
          <div className="text-center py-12 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
              No content yet
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto">
              Add a module to get started.
            </p>
          </div>
        </div>
      </>
    );
  }

  const isCurrentCompleted = selectedLesson
    ? !!completedLessons[selectedLesson.id]
    : false;

  return (
    <>
      {editMode && (
        <div className="sticky top-0 z-30 h-10 flex items-center justify-between px-4 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-900/60 text-sm text-amber-900 dark:text-amber-100">
          <span className="font-medium">
            Editing as admin · changes are live
          </span>
          <button
            type="button"
            onClick={handleExitEdit}
            className="text-amber-900 dark:text-amber-100 hover:underline font-medium"
          >
            Exit
          </button>
        </div>
      )}
      <div className="flex flex-col md:flex-row h-[calc(100vh-8rem)] min-h-[600px] max-w-[1600px] mx-auto pb-20 md:pb-0">
        {/* Main Content */}
        <div className="flex-1 flex flex-col gap-4 min-w-0 overflow-auto px-4 md:px-6 py-4">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 truncate">
              {selectedLesson?.title || course.title}
            </h2>
            {editMode && course.status !== 'Published' && (
              <Badge variant="outline" className="text-[10px] shrink-0">
                {course.status}
              </Badge>
            )}
            {editMode && (
              <div className="ml-auto shrink-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Course editing actions"
                      className="min-w-[44px] min-h-[44px]"
                    >
                      <MoreVertical className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem
                      disabled={!selectedLesson}
                      onSelect={() => {
                        if (!selectedLesson || !editing) return;
                        editing.setEditingLessonId(selectedLesson.id);
                        editing.setIsLessonModalOpen(true);
                      }}
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit Lesson
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => setIsManageModulesOpen(true)}
                    >
                      <Layers className="h-4 w-4 mr-2" />
                      Manage Modules
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => editing?.setIsCourseModalOpen(true)}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Course Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={handleExitEdit}>
                      <Eye className="h-4 w-4 mr-2" />
                      Preview as Student
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>

          <PlayerVideoSection
            lesson={selectedLesson}
            hasPublishedLessons={publishedLessons.length > 0}
            onVideoEnded={handleVideoEnded}
          />

          {selectedLesson && (
            <>
              <PlayerControls
                isCompleted={isCurrentCompleted}
                previousLesson={previousLesson}
                nextLesson={nextLesson}
                onToggleComplete={handleToggleComplete}
                onPrevious={() => previousLesson && navigateToLesson(previousLesson)}
                onNext={() => nextLesson && navigateToLesson(nextLesson)}
                onCompleteAndNext={handleCompleteAndNext}
              />

              <PlayerLessonContent lesson={selectedLesson} />
            </>
          )}
        </div>

        {/* Desktop Sidebar */}
        <PlayerSidebarTOC
          course={course}
          selectedLessonId={selectedLesson?.id || null}
          expandedModules={expandedModules}
          completedLessons={completedLessons}
          progress={progress.percent}
          editMode={editMode}
          onToggleModule={toggleModule}
          onSelectLesson={navigateToLesson}
          onToggleLessonComplete={handleToggleLessonComplete}
        />

        {/* Mobile Bottom Nav */}
        <PlayerMobileNav
          hasPrevious={!!previousLesson}
          hasNext={!!nextLesson}
          onPrevious={() => previousLesson && navigateToLesson(previousLesson)}
          onNext={() => nextLesson && navigateToLesson(nextLesson)}
          onOpenContents={() => setShowMobileTOC(true)}
        />

        {/* Mobile TOC Sheet */}
        <PlayerMobileTOC
          open={showMobileTOC}
          onOpenChange={setShowMobileTOC}
          course={course}
          selectedLessonId={selectedLesson?.id || null}
          expandedModules={expandedModules}
          completedLessons={completedLessons}
          editMode={editMode}
          onToggleModule={toggleModule}
          onSelectLesson={navigateToLesson}
          onToggleLessonComplete={handleToggleLessonComplete}
          onEditLesson={
            editMode && editing
              ? (id) => {
                  editing.setEditingLessonId(id);
                  editing.setSelectedModule(null);
                  editing.setIsLessonModalOpen(true);
                  setShowMobileTOC(false);
                }
              : undefined
          }
          onManageModules={
            editMode && editing
              ? () => {
                  setIsManageModulesOpen(true);
                  setShowMobileTOC(false);
                }
              : undefined
          }
        />
      </div>

      {/* Lazy-loaded admin editing sheets — only mounted in edit mode. The
          chunks download the first time a sheet opens, then are cached. */}
      {editMode && editing && (
        <Suspense
          fallback={
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
              <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6">
                <LoadingSpinner className="h-6 w-6" />
              </div>
            </div>
          }
        >
          <LazyLessonEditSheet
            open={editing.isLessonModalOpen}
            onOpenChange={editing.setIsLessonModalOpen}
            editingLessonId={editing.editingLessonId}
            selectedModuleId={editing.selectedModule}
            onSuccess={editing.handleSheetSaveSuccess}
          />

          <LazyModuleEditSheet
            open={editing.isModuleModalOpen}
            onOpenChange={editing.setIsModuleModalOpen}
            editingModuleId={editing.editingModuleId}
            courseId={courseId}
            onSuccess={editing.handleSheetSaveSuccess}
          />

          <LazyCourseEditSheet
            open={editing.isCourseModalOpen}
            onOpenChange={editing.setIsCourseModalOpen}
            course={editing.course}
            selectedSubjectIds={editing.selectedSubjectIds}
            onSubjectIdsChange={editing.setSelectedSubjectIds}
            onSave={editing.handleUpdateCourse}
            updateLoading={editing.updateLoading}
          />

          {/* Manage Modules sheet — wraps CourseContentTree (drag-drop +
              inline add/edit) for the player overlay. CourseDetail.tsx
              renders the tree inline; here it lives inside a Sheet. */}
          <Sheet open={isManageModulesOpen} onOpenChange={setIsManageModulesOpen}>
            <SheetContent className="sm:max-w-2xl overflow-y-auto">
              <SheetHeader>
                <SheetTitle>Manage Modules</SheetTitle>
                <SheetDescription>
                  Drag to reorder · Click + to add
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4">
                <LazyCourseContentTree
                  modules={editing.modules}
                  loading={false}
                  error={null}
                  inlineEditingModuleId={editing.inlineEditingModuleId}
                  inlineEditingLessonId={editing.inlineEditingLessonId}
                  moduleInputRef={editing.newModuleInputRef}
                  lessonInputRefs={editing.newLessonInputRefs}
                  onDragEnd={editing.handleDragEnd}
                  onAddModuleInline={editing.handleAddModuleInline}
                  onModuleTitleChange={editing.handleModuleTitleChange}
                  onSaveModuleInline={editing.handleSaveModuleInline}
                  onCancelModuleInline={editing.handleCancelModuleInline}
                  onToggleModuleExpand={editing.handleToggleModuleExpand}
                  onStartEditModule={editing.handleStartEditModule}
                  onDeleteModule={(id, title) => {
                    editing.setDeleteModuleId(id);
                    editing.setDeleteModuleTitle(title);
                  }}
                  onAddLessonInline={editing.handleAddLessonInline}
                  onLessonTitleChange={editing.handleLessonTitleChange}
                  onSaveLessonInline={editing.handleSaveLessonInline}
                  onCancelLessonInline={editing.handleCancelLessonInline}
                  onOpenEditLessonModal={(lessonId) => {
                    editing.setEditingLessonId(lessonId);
                    editing.setSelectedModule(null);
                    editing.setIsLessonModalOpen(true);
                  }}
                  onOpenDeleteLessonDialog={(id, title) => {
                    editing.setDeleteLessonId(id);
                    editing.setDeleteLessonTitle(title);
                  }}
                />
              </div>
            </SheetContent>
          </Sheet>
        </Suspense>
      )}

      {/* Delete Module dialog — gated behind editMode so non-admins never
          render it. */}
      {editMode && editing && (
        <AlertDialog
          open={!!editing.deleteModuleId}
          onOpenChange={(open) => !open && editing.setDeleteModuleId(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Module</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete the module "{editing.deleteModuleTitle}"? This will also delete all lessons within it.
                <br /><br />
                <span className="font-semibold text-destructive">This action cannot be undone.</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={editing.handleDeleteModule}
                disabled={editing.deleteModuleLoading}
              >
                {editing.deleteModuleLoading ? (
                  <><LoadingSpinner className="mr-2 h-4 w-4" />Deleting...</>
                ) : (
                  'Delete Module'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Delete Lesson dialog */}
      {editMode && editing && (
        <AlertDialog
          open={!!editing.deleteLessonId}
          onOpenChange={(open) => !open && editing.setDeleteLessonId(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Lesson</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete the lesson "{editing.deleteLessonTitle}"?
                <br /><br />
                <span className="font-semibold text-destructive">This action cannot be undone.</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={editing.handleDeleteLesson}
                disabled={editing.deleteLessonLoading}
              >
                {editing.deleteLessonLoading ? (
                  <><LoadingSpinner className="mr-2 h-4 w-4" />Deleting...</>
                ) : (
                  'Delete Lesson'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
